import { useEffect, useState } from "react";
import { Leaf, Flame, RotateCcw } from "lucide-react";
import dayjs from "dayjs";
import useApiMethods from "../../../lib/useApiMethods";
import { LEAD_NURTURE_URL } from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";
import { dayLabel } from "../../email/NurtureCard";

const STATUS = {
    sending: ["Sending", "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300"],
    sent:    ["Sent", "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"],
    skipped: ["Not sent", "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"],
    failed:  ["Failed", "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"],
};
const UPCOMING = ["Upcoming", "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"];
const OFF = ["Off", "bg-gray-50 text-gray-400 dark:bg-gray-800 dark:text-gray-500"];
const OUTCOME = { revived: "Revived", won: "Won", closed: "Closed", left: "Ended" };

/**
 * Lead page: the lead's stay in Nurture - since when, why, the revisit date,
 * a warm signal (clicked, replied, enquired again), and the nurture steps with
 * what happened to each. "Revive" opens the normal status form preset to
 * Contacted. email/nurture/lead/<id>/ (platform_core/email_integration/
 * nurture.py). Shown while the lead is in Nurture; after that, a one-line note
 * of the last stay.
 */
export default function NurtureCard({ customerId, stage, onRevive }) {
    const { fetch } = useApiMethods();
    const [data, setData] = useState(null);

    useEffect(() => {
        let live = true;
        if (!customerId) return undefined;
        fetch(LEAD_NURTURE_URL(customerId)).then(res => {
            if (live && res?.status === "success") setData(res.data);
        });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, stage]);

    if (!data?.stay) return null;
    const { stay, schedule = [] } = data;
    const active = stay.status === "active";

    if (!active) {
        return (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                <Leaf className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400" aria-hidden="true" />
                Nurtured {dayjs(stay.started_at).format("D MMM YYYY")} – {dayjs(stay.ended_at).format("D MMM YYYY")}
                {stay.outcome && <> · {OUTCOME[stay.outcome] || stay.outcome}</>}
            </div>
        );
    }

    const revisitDue = stay.revisit_on && !dayjs(stay.revisit_on).isAfter(dayjs(), "day");
    return (
        <div className="rounded-xl border border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-900/20 p-4">
            <div className="flex items-center gap-1.5 mb-2">
                <Leaf className="h-3.5 w-3.5 text-teal-700 dark:text-teal-300" aria-hidden="true" />
                <p className="text-[10px] font-bold text-teal-700 dark:text-teal-300 uppercase tracking-widest flex-1">
                    Nurture Lead
                </p>
                {onRevive && (
                    <button type="button" onClick={onRevive}
                            className="flex items-center gap-1 text-[11px] font-semibold text-teal-700 dark:text-teal-200 hover:underline">
                        <RotateCcw className="h-3 w-3" aria-hidden="true" /> Revive
                    </button>
                )}
            </div>
            <p className="text-xs text-gray-800 dark:text-gray-100">
                Since {dayjs(stay.started_at).format("D MMM YYYY")}
                {stay.reason_label && <> · {stay.reason_label}</>}
            </p>
            {stay.revisit_on && (
                <p className={cn("text-xs mt-0.5", revisitDue ? "font-semibold text-amber-700 dark:text-amber-300" : "text-gray-600 dark:text-gray-300")}>
                    Revisit {revisitDue ? "due" : "on"} {dayjs(stay.revisit_on).format("D MMM YYYY")}
                </p>
            )}
            {stay.warm_at && (
                <p className="flex items-start gap-1 text-xs mt-2 text-orange-700 dark:text-orange-300">
                    <Flame className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden="true" />
                    <span><b>Warming up</b> · {dayjs(stay.warm_at).format("D MMM, h:mm A")}: {stay.warm_reason}</span>
                </p>
            )}
            {schedule.length > 0 && (
                <ul className="space-y-1 mt-2 border-t border-teal-100 dark:border-teal-800 pt-2">
                    {schedule.map((r, i) => {
                        const [label, cls] = r.status ? (STATUS[r.status] || [r.status, UPCOMING[1]]) : (r.switched_on ? UPCOMING : OFF);
                        return (
                            <li key={i} className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300">
                                <span className="w-16 shrink-0">{r.channel === "whatsapp" ? "WhatsApp" : "Email"}</span>
                                <span className="flex-1 min-w-0 truncate" title={r.reason || ""}>
                                    {r.day_offset == null ? "Removed step" : dayLabel(r.day_offset)} · {dayjs(r.sent_at || r.due_at).format("D MMM")}
                                </span>
                                <span className={cn("px-1.5 py-0.5 rounded-full text-[10px] font-medium", cls)}>{label}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
            {!data.enabled && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
                    Nurture messages are off (Settings → Email Templates → Lead nurture).
                </p>
            )}
        </div>
    );
}
